import type { i18n } from 'i18next';

import { isEmpty, isObject, omit } from 'lodash-es';
import { useMemo, type BaseSyntheticEvent, type FormEvent } from 'react';
import {
    createFormControl,
    type ErrorOption,
    type FieldValues,
    type Resolver,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { SwarmType } from '@/graphql/types';
import { shareReplayRefCount } from '../../../utils/rxjs';
import type { Profile } from '../../../profile';
import { useProfile } from '../../../profile';
import { NotImplementedError } from '../../../utils/errors';
import warning from '../../../utils/logger';
import { convertUnits, convertUnitsForDisplay } from '../../../utils/formatting';
import { useDeepCompareMemoize } from '../../../hooks/useDeepCompareMemoize';
import type { LayoutTree } from '../../reactForm/layout-helper';
import { LayoutHelper } from '../../reactForm/layout-helper';
import type { RhfProps } from '../../reactForm/types';
import { fromReactHookFormControl, OVERRIDES_NAME } from '../../reactForm/utils';
import { resolver, type ResolverContext } from '../../reactForm/validation';
import { DisabledConfigHelper } from './configHelper';
import type { ConfigHelper } from './configHelper';
import { ConfigurationWarningsHelper } from './ConfigurationWarning';
import { FieldNotFoundError } from './fields-array';
import type { FormValuesHandler } from './handlers';
import { createTranslatedFormSchema } from './schema-utils';
import type {
    FieldDefaultsByName,
    FormSchema,
    TranslatedFormField,
    TranslatedFormSchema,
} from './types';
import { NON_FIELD_ERRORS } from '../../reactForm/ErrorToNotificationsLink';

function hasOverrides(
    values: FieldValues
): values is FieldValues & { [OVERRIDES_NAME]: Record<string, unknown> } {
    // We intentionally disable the `no-unsafe-assignment` rule for this line.
    // This is the boundary where we are taking a value of type `any` (from FieldValues)
    // and starting the process of safely checking and narrowing its type.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    return isObject(values?.[OVERRIDES_NAME]);
}

export function configDistanceConverter(
    formSchema: FormSchema | TranslatedFormSchema,
    config: FieldValues,
    profile: Profile,
    revert = false
) {
    if (!config) {
        return config;
    }

    return Object.fromEntries(
        Object.entries(config).map(([fieldName, value]) => {
            let convertedValue: unknown = value;

            try {
                const fieldDefinition = formSchema.fields.getByName(fieldName);

                convertedValue = revert
                    ? convertUnits(fieldDefinition, value, profile, true)
                    : convertUnitsForDisplay(fieldDefinition, value, profile);
            } catch (error) {
                // Some fields in the form do not exist in the backend schema.
                // We expect these to be not found.
                if (!(error instanceof FieldNotFoundError)) {
                    throw error;
                }
            }

            return [fieldName, convertedValue];
        })
    );
}

export interface SuccessfulFormSubmission {
    id: string;
    addAnotherAfterSave?: boolean;
    entityName?: string;
    warnings?: string[];
}

export interface FormSubmissionOptions {
    addAnotherAfterSave?: boolean;
    saveButtonKey?: string | null;
}

export interface SwarmFormControllerConfig {
    swarmType: SwarmType;
    isProject: boolean;
    i18n: i18n;
    profile: Profile;
}

type SavePrivateNotesFunction = (input: {
    id: string;
    privateNotes: string;
}) => Promise<string | null>;

export abstract class SwarmFormController {
    id?: string;

    declare swarmType: SwarmType;

    declare isProject: boolean;

    declare formSchema: TranslatedFormSchema;

    declare profile: Profile;

    defaultValues: Partial<FieldDefaultsByName>;

    fields: TranslatedFormSchema['fields'];

    layout: TranslatedFormSchema['layout'];

    configHelper: ConfigHelper;

    layoutHelper: LayoutHelper;

    rhfProps: RhfProps<FieldValues, ResolverContext>;

    mainSaveIsInProgress$: BehaviorSubject<boolean>;

    privateNotesSaveIsInProgress$: BehaviorSubject<boolean>;

    activeSaveButtonKey: string | null;

    serverValuesUpdate$: BehaviorSubject<boolean>;

    serverValuesLoaded$: BehaviorSubject<boolean>;

    onSubmitSuccessful$: Subject<SuccessfulFormSubmission>;

    showAreYouSureToOverrideMessage: boolean;

    parentValues$: BehaviorSubject<FieldValues | null>;

    overriddenValuesBackup: Map<string, unknown>;

    formValueHandlers: FormValuesHandler[];

    configWarnings: ConfigurationWarningsHelper;

    declare finalStableValues$: Observable<FieldValues>;

    queries: unknown;

    privateNotesSaveFunction?: SavePrivateNotesFunction;

    constructor(config: SwarmFormControllerConfig) {
        this.storeConfig(config);

        this.fields = this.createFields();
        this.layout = Object.freeze(this.formSchema.layout.slice());
        this.configHelper = this.createConfigHelper();
        this.layoutHelper = this.createLayoutHelper();
        this.defaultValues = this.createDefaultValues();

        // This object breaks a circular dependency. The `resolver` needs access
        // to `getValues`, but `getValues` is only available after `useForm` has been
        // initialized. By passing this stable reference and mutating it later, we
        // make `getValues` available to the resolver when it runs.
        const validationSchema = this.createValidationSchema();
        const validationContext: ResolverContext = {
            configHelper: this.configHelper,
            validationSchema,
            distanceUnit: this.profile.distanceUnit,
        };

        this.rhfProps = createFormControl({
            mode: 'all',
            defaultValues: this.defaultValues,
            resolver: resolver as Resolver<FieldValues, ResolverContext>,
            shouldFocusError: true,
            context: validationContext,
        });

        // Attach `getValues` to the context object after `useForm` has run.
        validationContext.getValues = this.rhfProps.getValues;

        this.mainSaveIsInProgress$ = new BehaviorSubject(false);
        this.privateNotesSaveIsInProgress$ = new BehaviorSubject(false);
        this.activeSaveButtonKey = null;
        this.serverValuesUpdate$ = new BehaviorSubject(false);
        this.serverValuesLoaded$ = new BehaviorSubject(false);
        this.onSubmitSuccessful$ = new Subject<SuccessfulFormSubmission>();
        this.showAreYouSureToOverrideMessage = true;
        this.parentValues$ = new BehaviorSubject<FieldValues | null>(null);

        this.overriddenValuesBackup = new Map();

        // Initialize the chain.
        this.configWarnings = new ConfigurationWarningsHelper(this);
        this.formValueHandlers = [this.configHelper, this.configWarnings];

        // This method now sets up the single, powerful subscription.
        this.linkHandlersToReactFormHook();
    }

    storeConfig({ swarmType, isProject, i18n, profile }: SwarmFormControllerConfig) {
        this.swarmType = swarmType;
        this.isProject = isProject;
        this.profile = profile;

        this.formSchema = createTranslatedFormSchema(swarmType, isProject, i18n, profile);
    }

    static use(swarmType: SwarmType, isProject: boolean, extraConfig?: object) {
        // We do not use class components, warning below can be ignored.
        /* eslint-disable react-hooks/rules-of-hooks */
        const { i18n } = useTranslation();
        const profile = useProfile();
        const stableExtraConfig = useDeepCompareMemoize(extraConfig);

        return useMemo(() => {
            const extraConfigToSpread = stableExtraConfig ?? {};
            const Controller = this as unknown as new (
                config: SwarmFormControllerConfig & object
            ) => SwarmFormController;

            return new Controller({
                swarmType,
                isProject,
                i18n,
                profile,
                ...extraConfigToSpread,
            });
        }, [swarmType, isProject, i18n, profile, stableExtraConfig]);
        /* eslint-enable */
    }

    get information() {
        return this.formSchema.information as unknown as Record<string, string>;
    }

    createDefaultValues(): Partial<FieldDefaultsByName> {
        type FieldWithDefault = TranslatedFormField & { default: unknown };

        const entries = this.fields
            .filter((field): field is FieldWithDefault => 'default' in field)
            .map((field) => [field.name, field.default]);

        return Object.fromEntries(entries) as Partial<FieldDefaultsByName>;
    }

    createFields(): TranslatedFormSchema['fields'] {
        const newFields = this.formSchema.fields.slice();

        if (!this.isProject) {
            // Adds the Wi-Fi password field.
            newFields.insertAfter('project', {
                name: 'deviceWifiPassword',
            } as unknown as TranslatedFormField);
        }

        if (this.swarmType === SwarmType.Sound) {
            newFields.insertAfter('ldenGuideline', {
                name: 'ldenGuidelinePreview',
            } as unknown as TranslatedFormField);
        }

        return Object.freeze(newFields);
    }

    createConfigHelper(): ConfigHelper {
        return new DisabledConfigHelper();
    }

    createValidationSchema(): ResolverContext['validationSchema'] {
        return undefined;
    }

    /**
     * Links the form's value stream to a persistent chain of handlers for processing.
     *
     * This method creates a single, unified RxJS pipeline that implements a "Chain of
     * Responsibility." The initial `values$` stream is connected to the first handler.
     * The stable output of each handler is then persistently wired as the input
     * for the next handler in the chain.
     *
     * This ensures handlers are processed sequentially and that the entire chain
     * is managed by a single subscription, preventing memory leaks and providing
     * unified error handling.
     */
    linkHandlersToReactFormHook() {
        if (!this.formValueHandlers || this.formValueHandlers.length === 0) {
            return;
        }

        const values$ = fromReactHookFormControl(
            this.rhfProps.formControl,
            'values'
        ) as Observable<FieldValues>;

        // Use `reduce` to declaratively build the entire observable chain.
        // The accumulator (`inputStream$`) is the stream of stable values from the
        // previous handler in the chain.
        this.finalStableValues$ = this.formValueHandlers
            .reduce<Observable<FieldValues>>(
                (inputStream$, handler) =>
                    // Create a new "cold" observable. When subscribed to, it will permanently
                    // connect the input stream to the handler's logic and forward the
                    // handler's stable output to the next link in the chain.
                    new Observable<FieldValues>((subscriber) => {
                        // 1. Subscribe the `subscriber` of our new Observable directly to the
                        //    handler's output. This forwards all values, errors, and completions.
                        const outputSub = handler.stableValuesOutput$.subscribe(subscriber);

                        // 2. Subscribe to the incoming stream and pass values to the handler's
                        //    input.
                        const inputSub = inputStream$.subscribe((values) => {
                            handler.updateValues(values);
                        });

                        // The teardown logic ensures all intermediate subscriptions are cleaned up.
                        return () => {
                            inputSub.unsubscribe();
                            outputSub.unsubscribe();
                        };
                    }),
                // The chain is seeded with the initial stream of form values.
                values$
            )
            .pipe(shareReplayRefCount());

        this.finalStableValues$.subscribe();
    }

    createLayoutHelper(): LayoutHelper {
        return new LayoutHelper(this.layout, this.fields);
    }

    createLayoutTree(): LayoutTree {
        return this.layoutHelper.createTree();
    }

    createReadOnlyFields(): string {
        throw new NotImplementedError();
    }

    createFormFieldOverrides(): object | null {
        // Return null to make use of the `defaultFormFieldOverrides`.
        return null;
    }

    isOverridable$(_fieldName: string) {
        return of(false);
    }

    // Root fields that are never copied from the parent entity into this form.
    getNotInheritedFields(): readonly string[] {
        return [];
    }

    setSaveIsInProgress(isInProgress: boolean) {
        this.mainSaveIsInProgress$.next(isInProgress);
    }

    startSave(saveButtonKey: string | null = null): boolean {
        if (
            this.mainSaveIsInProgress$.getValue() ||
            this.privateNotesSaveIsInProgress$.getValue()
        ) {
            return false;
        }

        this.activeSaveButtonKey = saveButtonKey;
        this.setSaveIsInProgress(true);
        return true;
    }

    setServerValuesUpdate(isUpdating: boolean) {
        this.serverValuesUpdate$.next(isUpdating);
    }

    setParentValues(values: FieldValues | null) {
        this.parentValues$.next(values);
    }

    setOverriddenField(field: string, value: unknown, removeFromBackup = false) {
        this.rhfProps.setValue(`${OVERRIDES_NAME}.${field}`, value);

        // `removeFromBackup` is used by the `ChoiceField` to ensure that once a user selects
        // a non-project option, the selected option does not revert to the value that was set
        // before the project was chosen. This essentially prevents the behavior described below
        // in `Revert field values`.
        if (removeFromBackup) {
            this.overriddenValuesBackup.has(field) && this.overriddenValuesBackup.delete(field);
        }
    }

    async setValues(values: FieldValues) {
        if (isEmpty(values) || !isObject(values)) {
            return;
        }

        // Set a flag to indicate that the upcoming form changes are being made programmatically,
        // not by the user. The configuration warning logic uses this flag to decide whether to
        // suppress popups or to apply auto-fixes for these server-driven updates.
        this.setServerValuesUpdate(true);

        // Use the `reset` function to populate the form with new values. This is the standard
        // method recommended by React Hook Form (RHF) for this purpose.
        // See: https://github.com/react-hook-form/react-hook-form/blob/d8bb9b316a64840210b9f0611fdd8c74719f9920/src/useForm.ts#L156
        this.rhfProps.formControl.reset(omit(values, OVERRIDES_NAME));
        // Override needs to be assigned to a sub element.
        if (hasOverrides(values)) {
            Object.entries(values[OVERRIDES_NAME]).forEach(([name, value]) =>
                this.setOverriddenField(name, value, false)
            );
        }

        // Unset the flag to allow the configuration warning logic to react to subsequent
        // user-driven changes.
        this.setServerValuesUpdate(false);
        this.serverValuesLoaded$.next(true);

        // Manually trigger form validation after resetting with new server values.
        // This ensures that the newly populated form is immediately validated.
        // A built-in option for this in RHF's `reset` function was not apparent at the time of
        // writing.
        await this.rhfProps.formControl.trigger();
    }

    setGlobalError(key: string, message: ErrorOption['message']) {
        this.rhfProps.setError(`${NON_FIELD_ERRORS}.${key}`, { type: 'server', message });
    }

    setPrivateNotesSaveFunction(saveFunction?: SavePrivateNotesFunction) {
        this.privateNotesSaveFunction = saveFunction;
    }

    async savePrivateNotesOnly(privateNotes: string): Promise<string | null> {
        if (
            !this.id ||
            !this.privateNotesSaveFunction ||
            this.mainSaveIsInProgress$.getValue() ||
            this.privateNotesSaveIsInProgress$.getValue()
        ) {
            return null;
        }

        this.privateNotesSaveIsInProgress$.next(true);
        try {
            return await this.privateNotesSaveFunction({ id: this.id, privateNotes });
        } finally {
            this.privateNotesSaveIsInProgress$.next(false);
        }
    }

    commitPrivateNotesBaseline(privateNotes: string) {
        this.rhfProps.resetField('privateNotes', { defaultValue: privateNotes });
    }

    handleGenericServerError(error?: unknown) {
        warning(error || 'Generic server error');

        this.setGlobalError('serverError', gettext('GENERIC_SERVER_ERROR'));
    }

    // Overridden by subclasses to handle validated form submission values.
    // React Hook Form invokes this handler with the form values and the submit event.
    abstract onSubmitFunction(
        _values: FieldValues,
        _event?: BaseSyntheticEvent,
        _submissionOptions?: FormSubmissionOptions
    ): Promise<void> | void;

    async triggerSubmit(event: FormEvent, submissionOptions: FormSubmissionOptions = {}) {
        await this.rhfProps.handleSubmit((values, submitEvent) =>
            this.onSubmitFunction(values, submitEvent, submissionOptions)
        )(event);
    }

    askAreYouSureToOverrideMessageOnce() {
        const message = gettext('Are you sure you want to override the project settings?');
        if (this.showAreYouSureToOverrideMessage) {
            // eslint-disable-next-line no-restricted-globals, no-alert
            if (!confirm(message)) {
                return false;
            }

            this.showAreYouSureToOverrideMessage = false;
        }

        return true;
    }

    convertConfigValues(values: FieldValues, revert = false) {
        return configDistanceConverter(this.formSchema, values, this.profile, revert);
    }
}
